import "whatwg-fetch";

import Cookies from "js-cookie";
import { StringifiableRecord } from "query-string";
import type { PartialDeep } from "type-fest";

import { ErrorStatus } from "@/types/ResponseStatus";
import { stringifyUrl } from "@/utils";

/**
 * Misc Helpers
 * ==============================
 */
/**
 *
 * @returns The current organization
 */

const getCurrentOrganization = () => {
  const uuid = window.location.pathname.split("/")[2];
  if (/^[0-9A-Fa-f]{8}(-[0-9A-Fa-f]{4}){3}-[0-9A-Fa-f]{12}$/.test(uuid)) {
    return uuid;
  }
  return null;
};

/**
 * Constants
 * ==============================
 */

// Test that a content type is JSON
const JSON_REGEX = /\bapplication\/json\b/;

/**
 * Utils
 * ==============================
 */

/**
 * Utility type to recursively create error messages from another type
 * @param T The type to create error messages from
 * @returns A type with the same structure as T, but with all values replaced with string or string[]
 */
export type ErrorMessages<T> = PartialDeep<
  T extends object
    ? {
        [K in keyof T]: T[K] extends object
          ? ErrorMessages<T[K]>
          : string | string[];
      }
    : string | string[]
>;

/**
 * Request types
 * ==============================
 */

/**
 * Helper type to remove the body from a default request
 */
type BodilessRequest = Omit<RequestInit, "body">;

/**
 * GET requests can have a body, but it's added to the URL as query params
 *
 */
type ImplicitGetRequest<T extends StringifiableRecord | undefined> =
  BodilessRequest & {
    body?: T;
  };
type ExplicitGetRequest<T extends StringifiableRecord | undefined> =
  BodilessRequest & {
    method: "GET";
    body?: T;
  };

/**
 * Any request that has a method that is not GET
 * Non-GET requests can have a body, and it's added to the request body
 *
 * @param T The type of the body
 */
type NonGetRequest<T> = BodilessRequest & {
  method: "POST" | "PUT" | "DELETE" | "PATCH";
  body?: T;
};

/**
 * Options thru-type
 * Adapts to the request method
 * GET requests can have a body, but it's added to the URL as query params
 * Non-GET requests can have a body, and it's added to the request body
 * Body can never be pre-stringified
 *
 * @param T The type of the body\
 */
type Options<T> =
  | NonGetRequest<T>
  | ExplicitGetRequest<T extends StringifiableRecord ? T : never>
  | ImplicitGetRequest<T extends StringifiableRecord ? T : never>;

type FetcherOptions<T> = Options<T> & { timeout?: number };
/**
 * Return types
 * ==============================
 */

/** Typed call which has succeeded.
 * A success is determined by:
 * - a 2xx status code
 * - a truthy response.ok
 *
 * @param ExplicitResultType The type of the data returned by the API
 */
export interface SuccessfulResponse<ExplicitResultType> extends Response {
  ok: true;
  data: ExplicitResultType;
}

/** Typed call which has failed.
 * A failure is determined by either:
 * - a non-2xx status code
 * - a falsy response.ok
 *
 *  @param ExplicitErrorType The type of the error data returned by the API
 */
export interface ErrorResponse<ExplicitErrorType = unknown> extends Response {
  ok: false;
  status: ErrorStatus;
  data?: ExplicitErrorType;
}

/**
 * Fetch implementation
 * ==============================
 */

function cleanHeaders(
  base: Record<string, string | null | undefined>,
  extra?: HeadersInit
): HeadersInit {
  const out: Record<string, string> = {};

  // base first (so `extra` can override)
  for (const [k, v] of Object.entries(base)) {
    if (v != null) out[k] = v;
  }

  if (extra) {
    // Normalize any HeadersInit shape into an iterable via the Headers ctor
    const normalized = new Headers(extra);
    normalized.forEach((value, key) => {
      out[key] = value;
    });
  }
  return out;
}
/**
 * Fetcher V2
 * @param _url URL to fetch
 * @param options Object containing the request method, headers, and body
 * @returns A response object with the typed data attached
 *
 * Requests made with fetcher need to be checked for errors before you
 * are allowed to access the response data. This is a typescript guard
 * to enforce error handling.
 */
async function fetcher_v2<
  ExpectedResult = unknown,
  Body extends object | never = StringifiableRecord,
  ErrorResult = unknown,
>(_url: string, options?: FetcherOptions<Body>) {
  const { timeout, ...fetchOptions } = options ?? {};

  // If there is not set method fetch defaults to GET
  const isGET = (fetchOptions.method ?? "GET") === "GET";

  /* Allow passing body even though we're making a GET request.
   * These are automatically added onto the URL as query params
   */
  const url =
    fetchOptions.body != null && isGET
      ? stringifyUrl({
          url: _url,
          query: fetchOptions.body as StringifiableRecord,
        })
      : _url;

  const timeoutSignal =
    timeout != null ? AbortSignal.timeout(timeout) : undefined;
  const signal =
    timeoutSignal && fetchOptions.signal
      ? AbortSignal.any([fetchOptions.signal, timeoutSignal])
      : (timeoutSignal ?? fetchOptions.signal);

  // Construct the request and perform the fetch call

  const response = (await fetch(url, {
    ...fetchOptions,
    credentials: "include",
    headers: cleanHeaders(
      {
        "Content-Type": "application/json",
        "X-CSRFTOKEN": Cookies.get("csrftoken"),
        HTTP_REFERER: window.location.href,
        "AE-Organization": getCurrentOrganization(),
      },
      fetchOptions.headers
    ),
    body:
      /** Map the body to a JSON string if it exists and we're not making a GET request
       * ( GET has no body! */
      fetchOptions.body != null && !isGET
        ? JSON.stringify(fetchOptions.body)
        : undefined,
    ...(signal !== undefined ? { signal } : {}),
  })) as SuccessfulResponse<ExpectedResult> | ErrorResponse<ErrorResult>;

  /** attach the json/text result as data on the response,
   * skipping this step in every call, leaving the response ( almost ) intact */
  const isJSON = JSON_REGEX.test(response.headers.get("Content-Type") || "");
  response.data = await (isJSON ? response.json() : response.text());

  return response;
}

export default fetcher_v2;
